import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil, ImageUp, Trash } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { getShowEmployees, updateEmployeesData } from "@/Api/employees";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { Mask } from "@/utils/mask";
import { getSelectUnities } from "@/Api/unities";
import { getCet } from "@/Api/cep";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TypeUser } from "@/types/userType";
import { useAuth } from "@/hooks/auth";
import { HrFieldsStep, hrFieldsSchema, HR_FIELDS_OMIT_WHEN_EMPTY } from "@/components/HrFieldsStep";
import {
  PersonalDataStep,
  personalDataSchema,
  PERSONAL_DATA_OMIT_WHEN_EMPTY,
} from "@/components/PersonalDataStep";
import {
  DocumentsStep,
  documentsSchema,
  DOCUMENTS_OMIT_WHEN_EMPTY,
} from "@/components/DocumentsStep";
import {
  OtherInfoStep,
  otherInfoSchema,
  OTHER_INFO_OMIT_WHEN_EMPTY,
} from "@/components/OtherInfoStep";
import { EmployeeDocumentsManager } from "@/App/Employees/DocumentsManager";

const basicSchema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  email: z
    .string({ message: "*requerido" })
    .min(1, "*requerido")
    .email("Email inválido"),
  password: z.string().optional(),
  document: z.string({ message: "*requerido" }).min(1, "*requerido"),
  phone: z.string({ message: "*requerido" }).min(1, "*requerido"),
  photo: z.any(),
  unity_id: z.string({ message: "*requerido" }).min(1, "*requerido"),
});

const addressSchema = z.object({
  street: z.string().min(1, "*requerido").max(255),
  number: z.string().min(1, "*requerido").max(255),
  neighborhood: z.string().min(1, "*requerido").max(255),
  complement: z.string().max(255).optional(),
  state: z.string().min(1, "*requerido").max(255),
  city: z.string().min(1, "*requerido").max(255),
  zip_code: z.string().min(1, "*requerido").max(255),
});

const schema = z.object({
  ...basicSchema.shape,
  ...addressSchema.shape,
  ...hrFieldsSchema.shape,
  ...personalDataSchema.shape,
  ...documentsSchema.shape,
  ...otherInfoSchema.shape,
});

const OMIT_WHEN_EMPTY = [
  // Sem isso, um campo de senha nunca tocado (valor `undefined`) vira a STRING
  // literal "undefined" ao passar por FormData.append() — que satisfaz a regra
  // `min:8` do Laravel e sobrescreve a senha real do usuário silenciosamente.
  "password",
  ...HR_FIELDS_OMIT_WHEN_EMPTY,
  ...PERSONAL_DATA_OMIT_WHEN_EMPTY,
  ...DOCUMENTS_OMIT_WHEN_EMPTY,
  ...OTHER_INFO_OMIT_WHEN_EMPTY,
];

type SchemaType = z.infer<typeof schema>;

// Campo -> aba, pra levar o usuário até a aba com pendências ao enviar
const TAB_FIELDS: Record<string, string[]> = {
  basic: Object.keys(basicSchema.shape),
  address: Object.keys(addressSchema.shape),
  hr: Object.keys(hrFieldsSchema.shape),
  personal: Object.keys(personalDataSchema.shape),
  documents: Object.keys(documentsSchema.shape),
  other: Object.keys(otherInfoSchema.shape),
};
const TAB_LABELS: Record<string, string> = {
  basic: "Informações Básicas",
  address: "Endereço",
  hr: "Informações de RH",
  personal: "Dados Pessoais",
  documents: "Documentos",
  other: "Contratuais",
};

export const UpdateBtn = ({
  updateFront,
  uuid,
}: {
  updateFront: (response: any) => void;
  uuid: string;
}) => {
  const user = useAuth();
  const unityId = user?.user?.unity?.[0]?.id || "";
  const userTypeUnitReponsible =
    user?.user?.user_type_id == TypeUser.unity_responsible;

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      password: undefined,
      document: "",
      phone: "",
      photo: null,
      unity_id: userTypeUnitReponsible ? unityId : "",
      street: "",
      number: "",
      neighborhood: "",
      complement: "",
      state: "",
      city: "",
      zip_code: "",
      sector_id: "",
      position_id: "",
      work_schedule_id: "",
      max_daily_overtime_minutes: "",
      admission_date: "",
      father_name: "",
      mother_name: "",
      birth_date: "",
      birthplace: "",
      birthplace_state: "",
      nationality: "",
      gender: "",
      marital_status: "",
      ethnicity: "",
      education_level: "",
      mobile_phone: "",
      ctps_number: "",
      ctps_series: "",
      ctps_check_digit: "",
      ctps_state: "",
      ctps_issue_date: "",
      ctps_expiry_date: "",
      pis_pasep: "",
      rg_number: "",
      rg_issuing_agency: "",
      rg_state: "",
      rg_issue_date: "",
      rg_expiry_date: "",
      voter_registration_number: "",
      voter_registration_zone: "",
      voter_registration_section: "",
      military_certificate_number: "",
      military_certificate_series: "",
      military_certificate_type: "",
      military_certificate_category: "",
      military_csm_oam: "",
      military_rm_dn_comar: "",
      driver_license_number: "",
      professional_class_body: "",
      professional_class_issuer: "",
      professional_class_state: "",
      professional_class_issue_date: "",
      professional_class_expiry_date: "",
      immigration_arrival_year: "",
      immigration_visa_type: "",
      payment_frequency: "",
      termination_date: "",
      participates_in_cipa: false,
      last_medical_exam_date: "",
    },
  });

  const errorTabs = Object.keys(TAB_FIELDS).filter((key) =>
    TAB_FIELDS[key].some((field) => form.formState.errors[field as keyof SchemaType])
  );

  const formInvalid = (errors: Record<string, unknown>) => {
    const tabsWithError = Object.keys(TAB_FIELDS).filter((key) =>
      TAB_FIELDS[key].some((field) => errors[field])
    );
    if (tabsWithError.length < 1) return;
    if (!tabsWithError.includes(tab)) setTab(tabsWithError[0]);
    toast({
      description: `Preencha os campos obrigatórios em: ${tabsWithError
        .map((key) => TAB_LABELS[key])
        .join(", ")}.`,
      variant: "destructive",
    });
  };

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState("basic");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [unities, setUnities] = useState([]);

  useEffect(() => {
    if (isOpen) setTab("basic");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const loadUnities = async () => {
      const { data } = await getSelectUnities();
      setUnities(data?.response);
    };
    loadUnities();
  }, [isOpen]);

  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        const { data: responseApi } = await getShowEmployees(uuid);
        form.setValue("name", responseApi?.response?.user?.name || "");
        form.setValue("email", responseApi?.response?.user?.email || "");
        form.setValue(
          "document",
          Mask.maskCpfOrCnpj(responseApi?.response?.document || "")
        );
        form.setValue(
          "phone",
          Mask.maskPhone(responseApi?.response?.phone || "")
        );
        form.setValue("photo", responseApi?.response?.user?.photo || null);
        form.setValue(
          "unity_id",
          String(responseApi?.response?.unity_id) || ""
        );
        form.setValue("street", responseApi?.response?.address?.street || "");
        form.setValue("number", responseApi?.response?.address?.number || "");
        form.setValue(
          "neighborhood",
          responseApi?.response?.address?.neighborhood || ""
        );
        form.setValue(
          "complement",
          responseApi?.response?.address?.complement || ""
        );
        form.setValue("state", responseApi?.response?.address?.state || "");
        form.setValue("city", responseApi?.response?.address?.city || "");
        form.setValue(
          "zip_code",
          responseApi?.response?.address?.zip_code || ""
        );
        form.setValue(
          "sector_id",
          responseApi?.response?.sector_id
            ? String(responseApi.response.sector_id)
            : ""
        );
        form.setValue(
          "position_id",
          responseApi?.response?.position_id
            ? String(responseApi.response.position_id)
            : ""
        );
        form.setValue(
          "work_schedule_id",
          responseApi?.response?.work_schedule_id
            ? String(responseApi.response.work_schedule_id)
            : ""
        );
        form.setValue(
          "admission_date",
          responseApi?.response?.admission_date?.slice(0, 10) || ""
        );
        form.setValue(
          "max_daily_overtime_minutes",
          responseApi?.response?.max_daily_overtime_minutes != null
            ? String(responseApi.response.max_daily_overtime_minutes)
            : ""
        );

        const emp = responseApi?.response || {};
        // Dados Pessoais
        form.setValue("father_name", emp.father_name || "");
        form.setValue("mother_name", emp.mother_name || "");
        form.setValue("birth_date", emp.birth_date?.slice(0, 10) || "");
        form.setValue("birthplace", emp.birthplace || "");
        form.setValue("birthplace_state", emp.birthplace_state || "");
        form.setValue("nationality", emp.nationality || "");
        form.setValue("gender", emp.gender ? String(emp.gender) : "");
        form.setValue(
          "marital_status",
          emp.marital_status ? String(emp.marital_status) : ""
        );
        form.setValue("ethnicity", emp.ethnicity ? String(emp.ethnicity) : "");
        form.setValue(
          "education_level",
          emp.education_level ? String(emp.education_level) : ""
        );
        form.setValue("mobile_phone", emp.mobile_phone || "");
        // Documentos
        form.setValue("ctps_number", emp.ctps_number || "");
        form.setValue("ctps_series", emp.ctps_series || "");
        form.setValue("ctps_check_digit", emp.ctps_check_digit || "");
        form.setValue("ctps_state", emp.ctps_state || "");
        form.setValue(
          "ctps_issue_date",
          emp.ctps_issue_date?.slice(0, 10) || ""
        );
        form.setValue(
          "ctps_expiry_date",
          emp.ctps_expiry_date?.slice(0, 10) || ""
        );
        form.setValue("pis_pasep", emp.pis_pasep || "");
        form.setValue("rg_number", emp.rg_number || "");
        form.setValue("rg_issuing_agency", emp.rg_issuing_agency || "");
        form.setValue("rg_state", emp.rg_state || "");
        form.setValue("rg_issue_date", emp.rg_issue_date?.slice(0, 10) || "");
        form.setValue(
          "rg_expiry_date",
          emp.rg_expiry_date?.slice(0, 10) || ""
        );
        form.setValue(
          "voter_registration_number",
          emp.voter_registration_number || ""
        );
        form.setValue(
          "voter_registration_zone",
          emp.voter_registration_zone || ""
        );
        form.setValue(
          "voter_registration_section",
          emp.voter_registration_section || ""
        );
        form.setValue(
          "military_certificate_number",
          emp.military_certificate_number || ""
        );
        form.setValue(
          "military_certificate_series",
          emp.military_certificate_series || ""
        );
        form.setValue(
          "military_certificate_type",
          emp.military_certificate_type || ""
        );
        form.setValue(
          "military_certificate_category",
          emp.military_certificate_category || ""
        );
        form.setValue("military_csm_oam", emp.military_csm_oam || "");
        form.setValue("military_rm_dn_comar", emp.military_rm_dn_comar || "");
        form.setValue("driver_license_number", emp.driver_license_number || "");
        form.setValue(
          "professional_class_body",
          emp.professional_class_body || ""
        );
        form.setValue(
          "professional_class_issuer",
          emp.professional_class_issuer || ""
        );
        form.setValue(
          "professional_class_state",
          emp.professional_class_state || ""
        );
        form.setValue(
          "professional_class_issue_date",
          emp.professional_class_issue_date?.slice(0, 10) || ""
        );
        form.setValue(
          "professional_class_expiry_date",
          emp.professional_class_expiry_date?.slice(0, 10) || ""
        );
        // Imigração
        form.setValue(
          "immigration_arrival_year",
          emp.immigration_arrival_year ? String(emp.immigration_arrival_year) : ""
        );
        form.setValue("immigration_visa_type", emp.immigration_visa_type || "");
        // Contratuais e Outras Informações
        form.setValue(
          "payment_frequency",
          emp.payment_frequency ? String(emp.payment_frequency) : ""
        );
        form.setValue(
          "termination_date",
          emp.termination_date?.slice(0, 10) || ""
        );
        form.setValue("participates_in_cipa", !!emp.participates_in_cipa);
        form.setValue(
          "last_medical_exam_date",
          emp.last_medical_exam_date?.slice(0, 10) || ""
        );

        setImagePreview(responseApi?.response?.user?.photo || null);
      }
    };

    fetchData();
  }, [isOpen, uuid, form]);

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const cep = e.target.value.replace(/\D/g, "");

    if (cep.length === 8) {
      try {
        const { data } = await getCet(cep);
        if (!data.erro) {
          form.setValue("street", data.logradouro);
          form.setValue("neighborhood", data.bairro);
          form.setValue("city", data.localidade);
          form.setValue("state", data.uf);
        }
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
      }
    }
  };

  const formSubmit = async (data: SchemaType) => {
    try {
      setFormState("loading");
      const formData = new FormData();

      Object.keys(data).forEach((key) => {
        if (key === "photo") {
          if (data[key] instanceof File) {
            formData.append(key, data[key]);
          }
          return;
        }
        // FormData converte boolean pra string "true"/"false" via toString(), mas
        // a regra `boolean` do Laravel só aceita 1/0/"1"/"0" (não "true"/"false").
        if (typeof data[key] === "boolean") {
          formData.append(key, data[key] ? "1" : "0");
          return;
        }
        if (OMIT_WHEN_EMPTY.includes(key) && !data[key]) {
          return;
        }
        formData.append(key, data[key]);
      });

      const { data: apiResponse, ok } = await updateEmployeesData(uuid, formData);
      if (!ok) {
        throw new Error("Erro ao atualizar colaborador");
      }

      updateFront(apiResponse?.response);
      setFormState("default");
      form.reset();
      setIsOpen(false);
      toast({
        description: `Colaborador atualizado com sucesso!`,
        action: <Check color="#1edc2b" />,
      });
    } catch (error) {
      setFormState("error");
      toast({
        description: `Erro ao atualizar colaborador`,
        variant: "destructive",
      });

      setTimeout(() => {
        setFormState("default");
      }, 3000);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={() => setIsOpen(true)}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Editar Colaborador
          </DialogDescription>
        </DialogHeader>

        <Form
          onSubmit={form.handleSubmit(formSubmit, formInvalid)}
          {...form}
          className="space-y-3"
        >
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-3 h-auto">
              <TabsTrigger value="basic" className={cn(errorTabs.includes("basic") && "text-red-600")}>
                Informações Básicas
                {errorTabs.includes("basic") && " •"}
              </TabsTrigger>
              <TabsTrigger value="address" className={cn(errorTabs.includes("address") && "text-red-600")}>
                Endereço
                {errorTabs.includes("address") && " •"}
              </TabsTrigger>
              <TabsTrigger value="hr" className={cn(errorTabs.includes("hr") && "text-red-600")}>
                Informações de RH
                {errorTabs.includes("hr") && " •"}
              </TabsTrigger>
              <TabsTrigger value="personal" className={cn(errorTabs.includes("personal") && "text-red-600")}>
                Dados Pessoais
                {errorTabs.includes("personal") && " •"}
              </TabsTrigger>
              <TabsTrigger value="documents" className={cn(errorTabs.includes("documents") && "text-red-600")}>
                Documentos
                {errorTabs.includes("documents") && " •"}
              </TabsTrigger>
              <TabsTrigger value="other" className={cn(errorTabs.includes("other") && "text-red-600")}>
                Contratuais
                {errorTabs.includes("other") && " •"}
              </TabsTrigger>
            </TabsList>

            <div className="max-h-[60vh] overflow-y-auto pr-2 mt-3">
              <TabsContent value="basic" className="mt-0 space-y-3">
                <Form.Row>
                  <Form.Field name="photo">
                    {({ field }) => (
                      <Form.Item className="flex flex-col items-center gap-1">
                        <Form.Label>Perfil:</Form.Label>
                        <div className="flex items-center rounded-full size-32 border border-dashed border-gray-900/25 dark:border-gray-500 relative">
                          <div className="flex text-sm/6 text-gray-600 h-full w-full">
                            <label
                              htmlFor="employee-update-file-upload"
                              className="w-full h-full flex items-center justify-center cursor-pointer font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 group rounded-full overflow-hidden"
                            >
                              {imagePreview && (
                                <img
                                  className="absolute top-0 left-0 aspect-square object-contain w-full h-full -z-1 group-hover:brightness-75 transition rounded-full"
                                  src={imagePreview}
                                  alt="profile_image"
                                />
                              )}
                              <span className="flex gap-2 items-center relative z-50">
                                {!imagePreview ? (
                                  <ImageUp className="size-7 group-hover:text-blue-700" />
                                ) : (
                                  <ImageUp className="size-7 opacity-0 transition group-hover:opacity-100" />
                                )}
                              </span>
                              <input
                                id="employee-update-file-upload"
                                type="file"
                                className="sr-only"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    field.onChange(file);
                                    setImagePreview(URL.createObjectURL(file));
                                  }
                                }}
                              />
                            </label>
                          </div>
                        </div>
                        {imagePreview && (
                          <Button
                            type="button"
                            className="h-6 px-0 bg-transparent text-slate font-medium hover:text-red-600 hover:bg-transparent flex gap-1"
                            onClick={() => {
                              form.setValue("photo", null);
                              setImagePreview(null);
                            }}
                          >
                            <Trash className="size-5" />
                            Remover
                          </Button>
                        )}
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="name">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Nome <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Insira o nome" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="email">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Email <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            placeholder="Insira o email"
                            type="email"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="password">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Senha <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            placeholder="Insira a senha"
                            type="password"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="document">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Documento <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            className="mt-1"
                            typeMask="cpfOrCnpj"
                            placeholder="Insira o número de documentação"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="phone">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Telefone <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            typeMask="phone"
                            placeholder="Insira o telefone"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                {!userTypeUnitReponsible && (
                  <Form.Row>
                    <Form.Field name="unity_id">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Unidade <Form.Message />
                          </Form.Label>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                            <SelectContent>
                              {unities.map((unity: any) => (
                                <SelectItem
                                  key={unity.id}
                                  value={String(unity.id)}
                                >
                                  {unity.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>
                )}
              </TabsContent>

              <TabsContent value="address" className="mt-0 space-y-3">
                <Form.Row>
                  <Form.Field name="zip_code">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          CEP <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            placeholder="CEP"
                            onChange={(e) => {
                              field.onChange(e);
                              handleCepChange(e);
                            }}
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="street">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Rua <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Rua" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>

                  <Form.Field name="number">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Número <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Número" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="neighborhood">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Bairro <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Bairro" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>

                  <Form.Field name="complement">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Complemento <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Complemento" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="state">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Estado <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Estado" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>

                  <Form.Field name="city">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Cidade <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Cidade" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </TabsContent>

              <TabsContent value="hr" className="mt-0 space-y-3">
                <HrFieldsStep isOpen={isOpen} />
              </TabsContent>

              <TabsContent value="personal" className="mt-0 space-y-3">
                <PersonalDataStep />
              </TabsContent>

              <TabsContent value="documents" className="mt-0 space-y-3">
                <DocumentsStep />
                <div className="pt-4 border-t">
                  <EmployeeDocumentsManager employeeUuid={uuid} />
                </div>
              </TabsContent>

              <TabsContent value="other" className="mt-0 space-y-3">
                <OtherInfoStep />
              </TabsContent>
            </div>
          </Tabs>

          <DialogFooter>
            <Button
              type="submit"
              className={cn("w-full", colors.bg_primary)}
              disabled={formState !== "default"}
            >
              {formState === "loading" && (
                <>
                  Atualizando <LoaderSpin />
                </>
              )}
              {formState === "default" && "Atualizar"}
              {formState === "error" && "Erro ao atualizar"}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
